<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { createReusableTemplate } from '@vueuse/core'
import { debounce } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useDetectDevice } from '@/composables/device'
import { usePdfPreview } from '@/composables/pdf-preview'
import { usePrintTemplates, pickPrintTemplateSettings, printTemplateAccentColors } from '@/composables/print-template'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PdfPreviewPage from '@/components/shared/PdfPreviewPage.vue'
import PrintTemplateThumbnail from '@/components/accounting/PrintTemplateThumbnail.vue'
import PrintLayoutPicker from '@/components/accounting/PrintLayoutPicker.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['cancel', 'update', 'delete', 'duplicate', 'set-default'])

const STEPS = Object.freeze(['brand', 'header', 'table', 'totals'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { isDefault, isDefaultFor, setDefaultLabel } = usePrintTemplates()
const [DefinePreviewDocumentToggle, ReusePreviewDocumentToggle] = createReusableTemplate()
const { QUOTE, INVOICE } = accountingStore.printDocumentTypeEnum

const language = portalStore.selectedLanguage
const translation = props.modelValue.translations?.[language] ?? {}

const editItem = ref({
  ...pickPrintTemplateSettings(props.modelValue),
  name: props.modelValue.name,
  footer_text: translation.footer_text ?? '',
})
const { isUpdated, isUpdating } = useIsUpdated(editItem)

const step = ref(STEPS[0])
const moveStep = (offset) => { step.value = STEPS[STEPS.indexOf(step.value) + offset] }
const stepDone = (name) => name !== step.value

const toOptions = (items) => items.map(({ id, name }) => ({ value: id, label: name }))
const fontOptions = toOptions(accountingStore.getLocalizedPrintFonts())
const logoPositionOptions = toOptions(accountingStore.getLocalizedPrintLogoPositions())
const logoSizeOptions = toOptions(accountingStore.getLocalizedPrintLogoSizes())
const totalsStyleOptions = toOptions(accountingStore.getLocalizedPrintTotalsStyles())
const documentTypeOptions = [
  { value: QUOTE, label: i18n.t('common.quote') },
  { value: INVOICE, label: i18n.t('common.invoice') },
]

const previewDocumentType = ref(QUOTE)
const previewParams = computed(() => ({
  ...pickPrintTemplateSettings(editItem.value),
  footer_text: editItem.value.footer_text,
  document_type: previewDocumentType.value,
}))
const { source: previewSource, isLoading: isPreviewLoading, refreshAsync: refreshPreviewAsync } = usePdfPreview(
  () => accountingStore.previewDocumentPrintTemplateAsync(previewParams.value)
)

const showPreviewPage = ref(false)
const isPreviewVisible = computed(() => !isMobile.value || showPreviewPage.value)
let renderedPreviewKey = null

const refreshVisiblePreview = () => {
  const previewKey = JSON.stringify(previewParams.value)
  if (!isPreviewVisible.value || previewKey === renderedPreviewKey) return
  renderedPreviewKey = previewKey
  refreshPreviewAsync()
}

watch(previewParams, debounce(refreshVisiblePreview, webSiteStore.getInputDebounce()))
onMounted(refreshVisiblePreview)

const onOpenPreview = () => {
  showPreviewPage.value = true
  refreshVisiblePreview()
}

const onUpdate = async () => {
  isUpdating.value = true
  try {
    const { name, footer_text: footerText, ...settings } = editItem.value
    await accountingStore.upsertDocumentPrintTemplateAsync({
      id: props.modelValue.id,
      ...settings,
      translations: { ...props.modelValue.translations, [language]: { name, footer_text: footerText || null } },
    })
    notifySuccess(i18n.t('info.updatedSuccessfully'))
    emit('update')
  } finally {
    isUpdating.value = false
  }
}
</script>
<template>
  <q-card class="dialog-card-flex" :style="isMobile ? undefined : 'height: 85vh;'">
    <define-preview-document-toggle>
      <q-btn-toggle
        v-model="previewDocumentType"
        :options="documentTypeOptions"
        size="sm"
        no-caps
        unelevated
        toggle-color="primary"
      />
    </define-preview-document-toggle>
    <navigate-back
      :text="`${i18n.t('common.edit')} · ${modelValue.name}`"
      @cancel="step === STEPS[0] ? emit('cancel') : moveStep(-1)"
    >
      <template v-if="isMobile" #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            flat
            round
            icon="mdi-eye-outline"
            @click="onOpenPreview"
          />
          <q-btn flat round icon="mdi-dots-vertical">
            <q-menu anchor="bottom right" self="top right">
              <q-list style="min-width: 12.5rem">
                <q-item v-close-popup clickable @click="emit('duplicate')">
                  <q-item-section avatar><q-icon name="mdi-content-copy" /></q-item-section>
                  <q-item-section>{{ i18n.t('common.duplicate') }}</q-item-section>
                </q-item>
                <q-item
                  v-for="option in documentTypeOptions"
                  :key="option.value"
                  v-close-popup
                  clickable
                  :disable="isDefaultFor(modelValue, option.value)"
                  @click="emit('set-default', option.value)"
                >
                  <q-item-section avatar><q-icon name="mdi-star-outline" /></q-item-section>
                  <q-item-section>{{ setDefaultLabel(option.value) }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </template>
    </navigate-back>
    <q-card-section class="col q-pa-none row print-template-edit">
      <div class="scroll dialog-card-content print-template-edit-form" :class="isMobile ? 'col-12' : 'col-6'">
        <div v-if="isMobile" class="row items-center no-wrap q-gutter-x-md q-pa-sm bg-grey-2 print-template-edit-thumbnail">
          <print-template-thumbnail class="print-template-edit-thumbnail-sheet" :template="editItem" :document-type="previewDocumentType" />
          <div class="text-caption text-grey-7">{{ i18n.t('info.printTemplateThumbnailHint') }}</div>
        </div>
        <q-stepper
          v-model="step"
          vertical
          flat
          class="q-py-none"
        >
          <q-step
            :name="STEPS[0]"
            :title="i18n.t('common.printTemplateStyle')"
            icon="mdi-palette-outline"
            :done="stepDone(STEPS[0])"
            :header-nav="true"
          >
            <q-input v-model="editItem.name" :label="i18n.t('common.name')" :rules="[val => !!val?.trim() || i18n.t('common.required')]" />
            <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.printTemplateBaseLayout') }}</div>
            <print-layout-picker
              v-model="editItem.layout"
              :template-for="layout => ({ ...editItem, layout })"
              :document-type="previewDocumentType"
            />
            <div class="text-caption text-grey-7 q-mt-md q-mb-xs">{{ i18n.t('common.printTemplateAccentColor') }}</div>
            <div class="row items-center q-gutter-sm">
              <button
                v-for="color in printTemplateAccentColors"
                :key="color"
                type="button"
                class="print-template-swatch"
                :class="{ selected: editItem.accent_color.toUpperCase() === color }"
                :style="{ backgroundColor: color }"
                :aria-label="color"
                @click="editItem.accent_color = color"
              />
              <label class="row items-center q-gutter-x-xs text-caption text-grey-7">
                <span>{{ i18n.t('common.custom') }}</span>
                <input v-model="editItem.accent_color" type="color" class="print-template-color-input">
              </label>
            </div>
            <div class="text-caption text-grey-7 q-mt-md q-mb-xs">{{ i18n.t('common.printTemplateFont') }}</div>
            <q-btn-toggle
              v-model="editItem.font"
              :options="fontOptions"
              no-caps
              unelevated
              toggle-color="primary"
            />
            <stepper-navigation :continue="() => moveStep(1)" />
          </q-step>

          <q-step
            :name="STEPS[1]"
            :title="i18n.t('common.printTemplateHeader')"
            icon="mdi-page-layout-header"
            :done="stepDone(STEPS[1])"
            :header-nav="true"
          >
            <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.printTemplateLogoPosition') }}</div>
            <q-btn-toggle
              v-model="editItem.logo_position"
              :options="logoPositionOptions"
              no-caps
              unelevated
              toggle-color="primary"
            />
            <div class="text-caption text-grey-7 q-mt-md q-mb-xs">{{ i18n.t('common.printTemplateLogoSize') }}</div>
            <q-btn-toggle
              v-model="editItem.logo_size"
              :options="logoSizeOptions"
              no-caps
              unelevated
              toggle-color="primary"
            />
            <q-toggle v-model="editItem.show_company_details" class="q-mt-md" :label="i18n.t('common.printTemplateShowCompanyDetails')" />
            <info-box class="q-mt-md" icon="mdi-lock-outline" :message="i18n.t('info.printTemplateCompanyIdentity')" />
            <stepper-navigation :back="() => moveStep(-1)" :continue="() => moveStep(1)" />
          </q-step>

          <q-step
            :name="STEPS[2]"
            :title="i18n.t('common.printTemplateTable')"
            icon="mdi-table"
            :done="stepDone(STEPS[2])"
            :header-nav="true"
          >
            <div class="column">
              <q-toggle v-model="editItem.zebra_rows" :label="i18n.t('common.printTemplateZebraRows')" />
            </div>
            <info-box class="q-mt-md" icon="mdi-lock-outline" :message="i18n.t('info.printTemplateRows')" />
            <stepper-navigation :back="() => moveStep(-1)" :continue="() => moveStep(1)" />
          </q-step>

          <q-step
            :name="STEPS[3]"
            :title="i18n.t('common.printTemplateTotalsAndFooter')"
            icon="mdi-page-layout-footer"
            :done="stepDone(STEPS[3])"
            :header-nav="true"
          >
            <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.printTemplateTotalsStyle') }}</div>
            <q-btn-toggle
              v-model="editItem.totals_style"
              :options="totalsStyleOptions"
              no-caps
              unelevated
              toggle-color="primary"
            />
            <div class="column q-mt-md">
              <div class="row items-center no-wrap">
                <q-toggle v-model="editItem.highlight_payment_details" :label="i18n.t('common.printTemplateHighlightPaymentDetails')" />
                <q-chip
                  dense
                  size="sm"
                  color="info"
                  text-color="white"
                  :label="i18n.t('common.printTemplateInvoicesOnly')"
                />
              </div>
            </div>
            <q-input v-model="editItem.footer_text" :label="i18n.t('common.printTemplateFooterText')" />
            <q-toggle v-model="editItem.show_powered_by" class="q-mt-sm" :label="i18n.t('common.printTemplateShowPoweredBy')" />
            <stepper-navigation :back="() => moveStep(-1)" />
          </q-step>
        </q-stepper>
      </div>

      <div v-if="!isMobile" class="print-template-edit-preview column q-pa-md q-gutter-y-sm col-6">
        <div class="row items-center justify-between">
          <span class="text-caption text-uppercase text-grey-7">{{ i18n.t('common.printTemplateLivePreview') }}</span>
          <reuse-preview-document-toggle />
        </div>
        <pdf-preview-panel class="col" :source="previewSource" :loading="isPreviewLoading" />
      </div>
    </q-card-section>

    <q-card-actions v-if="!isMobile || isUpdated" class="align-center dialog-card-bottom-bar">
      <q-btn
        v-if="!isMobile"
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        :disable="isDefault(modelValue)"
        @click="emit('delete')"
      >
        <q-tooltip v-if="isDefault(modelValue)">{{ i18n.t('error.validation.printTemplateDefaultDelete') }}</q-tooltip>
      </q-btn>
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        :disable="!editItem.name?.trim()"
        @click="onUpdate"
      />
    </q-card-actions>
    <q-dialog v-model="showPreviewPage" maximized>
      <pdf-preview-page
        :title="i18n.t('common.preview')"
        :source="previewSource"
        :loading="isPreviewLoading"
        @cancel="showPreviewPage = false"
      >
        <template #right>
          <reuse-preview-document-toggle />
        </template>
      </pdf-preview-page>
    </q-dialog>
  </q-card>
</template>
<style scoped lang="scss">
.print-template-edit {
  min-height: 0;
}

.print-template-edit-form {
  max-height: 100%;
}

.print-template-edit-thumbnail {
  position: sticky;
  top: 0;
  z-index: 1;
}

.print-template-edit-thumbnail-sheet {
  width: 3.5rem;
  flex-shrink: 0;
}

.print-template-edit-preview {
  min-height: 30rem;
  background-color: $grey-4;
}

.print-template-swatch {
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: .125rem solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .12);
  cursor: pointer;

  &.selected {
    box-shadow: 0 0 0 .125rem $dark;
  }
}

.print-template-color-input {
  width: 2rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: .25rem;
  background-color: #fff;
  cursor: pointer;
}
</style>
